import React, { useState } from 'react'
import {
	Box,
	Card,
	CardContent,
	Typography,
	TextField,
	Grid,
	Button,
	Alert,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from '@mui/material'

type PatternConfig = {
	tempFull: number
	tempClose: number
}

// 保存済み値が無いときのフォールバック。backend (app/models/control.rb) 既定に合わせる。
const HEATING_DEFAULT: PatternConfig = { tempFull: 10, tempClose: 20 }
const COOLING_DEFAULT: PatternConfig = { tempClose: 20, tempFull: 30 }

// backend (min < max) と同じ制約を submit 前に検査する。
// 加温: tempFull(=heating_temp_min) < tempClose(=heating_temp_max)
// 冷却: tempClose(=cooling_temp_min) < tempFull(=cooling_temp_max)
const validateConfig = (
	pattern: 'heating' | 'cooling',
	config: PatternConfig,
): string | null => {
	const { tempFull, tempClose } = config

	if (!isFinite(tempFull) || !isFinite(tempClose)) {
		return '温度を数値で入力してください'
	}

	const [min, max] =
		pattern === 'heating' ? [tempFull, tempClose] : [tempClose, tempFull]

	if (min === max) {
		return '全開温度と全閉温度に同じ値は指定できません（開度が線形に変化せず 0% / 100% の切り替わりになります）'
	}
	if (min > max) {
		return pattern === 'heating'
			? '全開温度は全閉温度より低い値にしてください'
			: '全閉温度は全開温度より低い値にしてください'
	}

	return null
}

type Props = {
	disabled?: boolean
	onSave?: (pattern: 'heating' | 'cooling', config: PatternConfig) => void
	initialHeating?: PatternConfig
	initialCooling?: PatternConfig
}

const TwaySimpleControlForm: React.FC<Props> = ({
	disabled = false,
	onSave,
	initialHeating,
	initialCooling,
}) => {
	const [heatingConfig, setHeatingConfig] = useState<PatternConfig>(
		initialHeating || HEATING_DEFAULT,
	)

	const [coolingConfig, setCoolingConfig] = useState<PatternConfig>(
		initialCooling || COOLING_DEFAULT,
	)

	const handleHeatingChange = (field: keyof PatternConfig, value: number) => {
		setHeatingConfig({ ...heatingConfig, [field]: value })
	}

	const handleCoolingChange = (field: keyof PatternConfig, value: number) => {
		setCoolingConfig({ ...coolingConfig, [field]: value })
	}

	const handleSaveClick = (pattern: 'heating' | 'cooling') => {
		const config = pattern === 'heating' ? heatingConfig : coolingConfig
		// ボタンは無効化済みだが、キーボード操作等での抜けを防ぐため二重に弾く
		if (validateConfig(pattern, config)) return
		if (onSave) {
			onSave(pattern, config)
		}
	}

	// リセットは保存済みの値（initial）に戻す。無い場合のみ既定値。
	const handleReset = (pattern: 'heating' | 'cooling') => {
		if (pattern === 'heating') {
			setHeatingConfig(initialHeating || HEATING_DEFAULT)
		} else {
			setCoolingConfig(initialCooling || COOLING_DEFAULT)
		}
	}

	// 加温モードの開度を計算
	const calculateHeatingOpening = (temp: number): number => {
		const { tempFull, tempClose } = heatingConfig
		if (temp <= tempFull) return 100
		if (temp >= tempClose) return 0
		// 線形補間
		return Math.round(((tempClose - temp) / (tempClose - tempFull)) * 100)
	}

	// 冷却モードの開度を計算
	const calculateCoolingOpening = (temp: number): number => {
		const { tempFull, tempClose } = coolingConfig
		if (temp <= tempClose) return 0
		if (temp >= tempFull) return 100
		// 線形補間
		return Math.round(((temp - tempClose) / (tempFull - tempClose)) * 100)
	}

	// min, maxの指定に対して10段階になるようstepを計算し、両端に2つ余分に表示
	const generateTempRange = (min: number, max: number): number[] => {
		// 異常値のチェック
		if (!isFinite(min) || !isFinite(max)) {
			return [0]
		}

		// min と max が同じ、または逆転している場合
		if (min === max) {
			return [min]
		}
		if (max < min) {
			return [min]
		}

		const step = (max - min) / 10

		// step が極端に小さい場合のガード（0.01未満は異常とみなす）
		if (step < 0.01) {
			return [min, max]
		}

		const start = min - 2 * step
		const end = max + 2 * step
		const range: number[] = []

		// ループの最大回数を制限（通常は14回だが、安全のため100回に制限）
		let count = 0
		const maxIterations = 100

		for (
			let temp = start;
			temp <= end + 0.001 && count < maxIterations;
			temp += step, count++
		) {
			// 小数点の精度を考慮して丸める（0.1刻み）
			range.push(Math.round(temp * 10) / 10)
		}

		return range
	}

	const heatingTempRange = generateTempRange(
		Math.min(heatingConfig.tempFull, heatingConfig.tempClose),
		Math.max(heatingConfig.tempFull, heatingConfig.tempClose),
	)

	const coolingTempRange = generateTempRange(
		Math.min(coolingConfig.tempClose, coolingConfig.tempFull),
		Math.max(coolingConfig.tempClose, coolingConfig.tempFull),
	)

	// 加温モードの制御概要テキストを生成
	const getHeatingDescription = () => {
		const { tempFull, tempClose } = heatingConfig
		return {
			summary: `土中平均が${tempClose}℃以下の時は弁を開いて温水で温める`,
			detail: `${tempFull}℃で100%開 → ${tempClose}℃で0%閉（全閉）`,
		}
	}

	// 冷却モードの制御概要テキストを生成
	const getCoolingDescription = () => {
		const { tempFull, tempClose } = coolingConfig
		return {
			summary: `土中A-2-①(tmp3)が${tempClose}℃以上の時は弁を開いて冷水で冷やす`,
			detail: `${tempFull}℃で100%開 → ${tempClose}℃で0%閉（全閉）`,
		}
	}

	const heatingDesc = getHeatingDescription()
	const coolingDesc = getCoolingDescription()

	const heatingError = validateConfig('heating', heatingConfig)
	const coolingError = validateConfig('cooling', coolingConfig)

	// 変更があるかチェック
	const hasHeatingChanged =
		initialHeating &&
		(heatingConfig.tempFull !== initialHeating.tempFull ||
			heatingConfig.tempClose !== initialHeating.tempClose)

	const hasCoolingChanged =
		initialCooling &&
		(coolingConfig.tempFull !== initialCooling.tempFull ||
			coolingConfig.tempClose !== initialCooling.tempClose)

	return (
		<Box>
			<Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
				三方弁簡易制御設定
			</Typography>

			<Grid container spacing={3}>
				{/* パターン1: 加温モード */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<Typography variant="h4" sx={{ mr: 1 }}>
									🔥
								</Typography>
								<Box>
									<Typography variant="h6">パターン1 - 加温モード</Typography>
									<Typography variant="caption" color="text.secondary">
										温水で温める
									</Typography>
								</Box>
							</Box>

							<Alert severity="info" sx={{ mb: 2 }}>
								<strong>制御概要：</strong>
								{heatingDesc.summary}
								<br />
								{heatingDesc.detail}
							</Alert>

							<Grid container spacing={2} sx={{ mb: 2 }}>
								<Grid item xs={6}>
									<TextField
										fullWidth
										type="number"
										label="全開温度"
										value={heatingConfig.tempFull}
										onChange={(e) =>
											handleHeatingChange(
												'tempFull',
												parseFloat(e.target.value),
											)
										}
										disabled={disabled}
										error={Boolean(heatingError)}
										inputProps={{ step: 0.5 }}
										InputProps={{
											endAdornment: (
												<Typography variant="body2" color="text.secondary">
													°C
												</Typography>
											),
										}}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										fullWidth
										type="number"
										label="全閉温度"
										value={heatingConfig.tempClose}
										onChange={(e) =>
											handleHeatingChange(
												'tempClose',
												parseFloat(e.target.value),
											)
										}
										disabled={disabled}
										error={Boolean(heatingError)}
										inputProps={{ step: 0.5 }}
										InputProps={{
											endAdornment: (
												<Typography variant="body2" color="text.secondary">
													°C
												</Typography>
											),
										}}
									/>
								</Grid>
							</Grid>

							<Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
								温水モード - 温度と開度の対応表
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mb: 1 }}
							>
								※加温モードの温度は (tmp2+tmp3)/2 の平均値
							</Typography>
							<TableContainer component={Paper} sx={{ mb: 2 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>温度 (℃)</TableCell>
											{heatingTempRange.map((temp) => {
												const roundedFull =
													Math.round(heatingConfig.tempFull * 10) / 10
												const roundedClose =
													Math.round(heatingConfig.tempClose * 10) / 10
												const isTarget =
													temp === roundedFull || temp === roundedClose
												return (
													<TableCell
														key={temp}
														align="center"
														sx={{ fontWeight: isTarget ? 'bold' : 'normal' }}
													>
														{temp}
													</TableCell>
												)
											})}
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell>開度</TableCell>
											{heatingTempRange.map((temp) => {
												const roundedFull =
													Math.round(heatingConfig.tempFull * 10) / 10
												const roundedClose =
													Math.round(heatingConfig.tempClose * 10) / 10
												const isTarget =
													temp === roundedFull || temp === roundedClose
												return (
													<TableCell
														key={temp}
														align="center"
														sx={{ fontWeight: isTarget ? 'bold' : 'normal' }}
													>
														{calculateHeatingOpening(temp)}
													</TableCell>
												)
											})}
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>

							{heatingError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{heatingError}
								</Alert>
							)}

							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									variant="contained"
									color="error"
									fullWidth
									onClick={() => handleSaveClick('heating')}
									disabled={
										disabled || !hasHeatingChanged || Boolean(heatingError)
									}
								>
									保存
								</Button>
								<Button
									variant="outlined"
									fullWidth
									onClick={() => handleReset('heating')}
									disabled={disabled}
								>
									リセット
								</Button>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* パターン2: 冷却モード */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
								<Typography variant="h4" sx={{ mr: 1 }}>
									❄️
								</Typography>
								<Box>
									<Typography variant="h6">パターン2 - 冷却モード</Typography>
									<Typography variant="caption" color="text.secondary">
										冷水で冷やす
									</Typography>
								</Box>
							</Box>

							<Alert severity="info" sx={{ mb: 2 }}>
								<strong>制御概要：</strong>
								{coolingDesc.summary}
								<br />
								{coolingDesc.detail}
							</Alert>

							<Grid container spacing={2} sx={{ mb: 2 }}>
								<Grid item xs={6}>
									<TextField
										fullWidth
										type="number"
										label="全閉温度"
										value={coolingConfig.tempClose}
										onChange={(e) =>
											handleCoolingChange(
												'tempClose',
												parseFloat(e.target.value),
											)
										}
										disabled={disabled}
										error={Boolean(coolingError)}
										inputProps={{ step: 0.5 }}
										InputProps={{
											endAdornment: (
												<Typography variant="body2" color="text.secondary">
													°C
												</Typography>
											),
										}}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										fullWidth
										type="number"
										label="全開温度"
										value={coolingConfig.tempFull}
										onChange={(e) =>
											handleCoolingChange(
												'tempFull',
												parseFloat(e.target.value),
											)
										}
										disabled={disabled}
										error={Boolean(coolingError)}
										inputProps={{ step: 0.5 }}
										InputProps={{
											endAdornment: (
												<Typography variant="body2" color="text.secondary">
													°C
												</Typography>
											),
										}}
									/>
								</Grid>
							</Grid>

							<Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
								冷水モード - 温度と開度の対応表
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mb: 1 }}
							>
								※冷却モードの温度は土中A-2-①(tmp3)
							</Typography>
							<TableContainer component={Paper} sx={{ mb: 2 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>温度 (℃)</TableCell>
											{coolingTempRange.map((temp) => {
												const roundedFull =
													Math.round(coolingConfig.tempFull * 10) / 10
												const roundedClose =
													Math.round(coolingConfig.tempClose * 10) / 10
												const isTarget =
													temp === roundedFull || temp === roundedClose
												return (
													<TableCell
														key={temp}
														align="center"
														sx={{ fontWeight: isTarget ? 'bold' : 'normal' }}
													>
														{temp}
													</TableCell>
												)
											})}
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell>開度</TableCell>
											{coolingTempRange.map((temp) => {
												const roundedFull =
													Math.round(coolingConfig.tempFull * 10) / 10
												const roundedClose =
													Math.round(coolingConfig.tempClose * 10) / 10
												const isTarget =
													temp === roundedFull || temp === roundedClose
												return (
													<TableCell
														key={temp}
														align="center"
														sx={{ fontWeight: isTarget ? 'bold' : 'normal' }}
													>
														{calculateCoolingOpening(temp)}
													</TableCell>
												)
											})}
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>

							{coolingError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{coolingError}
								</Alert>
							)}

							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button
									variant="contained"
									color="primary"
									fullWidth
									onClick={() => handleSaveClick('cooling')}
									disabled={
										disabled || !hasCoolingChanged || Boolean(coolingError)
									}
								>
									保存
								</Button>
								<Button
									variant="outlined"
									fullWidth
									onClick={() => handleReset('cooling')}
									disabled={disabled}
								>
									リセット
								</Button>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	)
}

export default TwaySimpleControlForm
